/**
 * App.js  —  root component.
 *
 * Orchestrates the five sections defined in /sections/ using the
 * centralised useDataset hook and the api service layer.
 *
 * Sections:
 *   1. Upload / Data Source
 *   2. Sheets Overview      (tabs, KPIs, column types)
 *   3. Dashboard             (data preview table + auto-charts)
 *   4. Chat with Data        (sidebar / bottom-sheet Q&A)
 *   5. Export Summary        (PDF / CSV / Markdown)
 */
import React, { useState, useCallback, useRef, useEffect, Suspense, lazy } from 'react';
import './App.css';
import { Toaster, toast } from 'sonner';

/* ── Layout / shell ────────────────────────────────── */
import AppBar from './components/AppBar';
import SettingsDialog from './components/SettingsDialog';
import OnboardingTour from './components/OnboardingTour';
import CommandPalette from './components/CommandPalette';
import Footer from './components/Footer';
import Breadcrumb from './components/Breadcrumb';
import ConfirmDialog from './components/ConfirmDialog';

/* ── Sections (static for upload, lazy for rest) ───── */
import UploadSection from './sections/UploadSection';

const SheetsOverviewSection = lazy(() => import('./sections/SheetsOverviewSection'));
const DashboardSection = lazy(() => import('./sections/DashboardSection'));
const ChatSection = lazy(() => import('./sections/ChatSection'));
const ExportSection = lazy(() => import('./sections/ExportSection'));
const SheetComparison = lazy(() => import('./components/SheetComparison'));
const AnalysisHistory = lazy(() => import('./components/AnalysisHistory'));

/* ── State + API ───────────────────────────────────── */
import useDataset from './hooks/useDataset';
import useI18n from './hooks/useI18n';
import * as api from './services/api';

/* ── Section nav tabs ──────────────────────────────── */
import { Table2, BarChart3, Download, GitCompareArrows, History } from 'lucide-react';
import { Button } from './components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SectionFallback = () => (
  <div className="py-6">
    <div className="h-5 w-40 shimmer rounded mb-4" />
    <div className="h-32 shimmer rounded-xl" />
  </div>
);

function App() {
  const { t } = useI18n();

  /* ── Dataset state ──────────────────────────────── */
  const ds = useDataset();

  const SECTION_TABS = [
    { id: 'overview',  label: t('nav.overview'),  icon: Table2 },
    { id: 'dashboard', label: t('nav.dashboard'), icon: BarChart3 },
    { id: 'export',    label: t('nav.export'),    icon: Download },
  ];

  /* ── Section navigation ─────────────────────────── */
  const [activeSection, setActiveSection] = useState('overview');
  const overviewRef  = useRef(null);
  const dashboardRef = useRef(null);
  const exportRef    = useRef(null);

  /* ── Settings state ─────────────────────────────── */
  const [provider, setProvider]         = useState(() => sessionStorage.getItem('llm_provider') || 'openai');
  const [apiKey, setApiKey]             = useState(() => {
    const prov = sessionStorage.getItem('llm_provider') || 'openai';
    return sessionStorage.getItem(`${prov}_api_key`) || '';
  });
  const [selectedModel, setSelectedModel] = useState(() => {
    const prov = sessionStorage.getItem('llm_provider') || 'openai';
    if (prov === 'openrouter') {
      return sessionStorage.getItem('openrouter_model') || 'openai/gpt-4o-mini';
    }
    return prov === 'mistral' ? 'mistral-small-latest' : 'gpt-4o-mini';
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  /* ── Chat state (persisted per sheet) ───────────── */
  const [chatOpen, setChatOpen]         = useState(false);
  const [chatHistoryBySheet, setChatHistoryBySheet] = useState({});

  /* ── Command palette state ──────────────────────── */
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  /* ── Sheet comparison state ─────────────────────── */
  const [compareOpen, setCompareOpen] = useState(false);

  /* ── Confirm replace dialog state ───────────────── */
  const [confirmReplace, setConfirmReplace] = useState(null);

  /* ── Analysis history state ─────────────────────── */
  const [historyOpen, setHistoryOpen] = useState(false);

  /* ── Global keyboard shortcuts ──────────────────── */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setChatOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && commandPaletteOpen) {
        setCommandPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen]);

  const chatHistory = chatHistoryBySheet[ds.activeSheet] || [];

  const setChatHistory = useCallback((updater) => {
    setChatHistoryBySheet((prev) => {
      const current = prev[ds.activeSheet] || [];
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [ds.activeSheet]: next };
    });
  }, [ds.activeSheet]);

  /* ── Scroll to section on tab click ─────────────── */
  const scrollToSection = useCallback((id) => {
    setActiveSection(id);
    const refMap = { overview: overviewRef, dashboard: dashboardRef, export: exportRef };
    refMap[id]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [overviewRef, dashboardRef, exportRef]);

  /* ── Upload handlers with replace-confirmation ──── */
  const handleUpload = useCallback((file) => {
    if (ds.hasData) {
      setConfirmReplace({ kind: 'file', payload: file });
    } else {
      ds.loadFromUpload(file);
    }
  }, [ds]);

  const handleImportSheet = useCallback((url) => {
    if (ds.hasData) {
      setConfirmReplace({ kind: 'url', payload: url });
    } else {
      ds.loadFromGoogleSheet(url);
    }
  }, [ds]);

  const confirmReplaceAction = useCallback(() => {
    if (!confirmReplace) return;
    if (confirmReplace.kind === 'file') {
      ds.loadFromUpload(confirmReplace.payload);
    } else if (confirmReplace.kind === 'url') {
      ds.loadFromGoogleSheet(confirmReplace.payload);
    }
    setConfirmReplace(null);
  }, [confirmReplace, ds]);

  /* ── Chat handler (streaming) ───────────────────── */
  const handleSendMessage = useCallback(async (question) => {
    if (!apiKey) {
      toast.error('Please set your API key in Settings first.');
      setSettingsOpen(true);
      return;
    }
    if (!ds.datasetId || !ds.activeSheet) {
      toast.error('Please upload a file first.');
      return;
    }

    const userMsg = { role: 'user', content: question, timestamp: new Date().toISOString() };
    setChatHistory((prev) => [...prev, userMsg, { role: 'assistant', content: '', streaming: true, timestamp: new Date().toISOString() }]);

    try {
      await api.sendChatMessageStream({
        datasetId: ds.datasetId,
        sheetName: ds.activeSheet,
        question,
        provider,
        apiKey,
        model: selectedModel,
        onToken: (token) => {
          setChatHistory((prev) => {
            const msgs = [...prev];
            const lastIdx = msgs.length - 1;
            if (lastIdx >= 0 && msgs[lastIdx].streaming) {
              msgs[lastIdx] = { ...msgs[lastIdx], content: msgs[lastIdx].content + token };
            }
            return msgs;
          });
        },
        onDone: (model) => {
          setChatHistory((prev) => {
            const msgs = [...prev];
            const lastIdx = msgs.length - 1;
            if (lastIdx >= 0 && msgs[lastIdx].streaming) {
              msgs[lastIdx] = { ...msgs[lastIdx], streaming: false, model };
            }
            return msgs;
          });
        },
        onError: (err) => {
          setChatHistory((prev) => {
            const msgs = prev.filter((m) => !m.streaming || m.content);
            const lastIdx = msgs.length - 1;
            if (lastIdx >= 0 && msgs[lastIdx].streaming) {
              msgs[lastIdx] = { ...msgs[lastIdx], streaming: false, isError: true, content: msgs[lastIdx].content || `Error: ${err.message}` };
            } else {
              msgs.push({ role: 'assistant', content: `Error: ${err.message}`, isError: true, timestamp: new Date().toISOString() });
            }
            return msgs;
          });
          toast.error(err.message);
        },
      });
    } catch (err) {
      setChatHistory((prev) => {
        const updated = prev.filter((m) => !m.streaming);
        return [
          ...updated,
          { role: 'assistant', content: `Error: ${err.message}`, isError: true, timestamp: new Date().toISOString() },
        ];
      });
      toast.error(err.message);
    }
  }, [apiKey, provider, selectedModel, ds.datasetId, ds.activeSheet, setSettingsOpen, setChatHistory]);

  /* ── Render ─────────────────────────────────────── */
  return (
    <div className="app-container flex flex-col min-h-screen">
      {/* Skip to content — accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>

      <Toaster position="top-right" richColors />

      {/* ── Top bar ──────────────────────────────── */}
      <AppBar
        filename={ds.filename}
        sheetCount={ds.sheetNames.length}
        onSettingsOpen={() => setSettingsOpen(true)}
        onChatToggle={() => setChatOpen(!chatOpen)}
        chatOpen={chatOpen}
        hasData={ds.hasData}
      />

      <main id="main-content" role="main" className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 flex-1 w-full">

        {/* ── Section 1: Upload / Data Source ────── */}
        {!ds.hasData && (
          <>
            <UploadSection
              onUpload={handleUpload}
              onImportSheet={handleImportSheet}
              loading={ds.loading}
              error={ds.error}
            />
            {/* Show history below upload when no data */}
            <div className="mt-6 max-w-lg mx-auto">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryOpen((v) => !v)}
                className="w-full gap-2 text-muted-foreground mb-3"
              >
                <History className="w-4 h-4" />
                {t('history.title')}
              </Button>
              {historyOpen && (
                <Suspense fallback={<SectionFallback />}>
                  <AnalysisHistory
                    onLoadDataset={ds.loadFromHistory}
                    currentDatasetId={ds.datasetId}
                    onClose={() => setHistoryOpen(false)}
                  />
                </Suspense>
              )}
            </div>
          </>
        )}

        {ds.hasData && (
          <div className="py-4">
            <UploadSection
              onUpload={handleUpload}
              onImportSheet={handleImportSheet}
              loading={ds.loading}
              error={ds.error}
              compact
            />
          </div>
        )}

        {/* ── Breadcrumb ─────────────────────────── */}
        {ds.hasData && ds.currentSheetInfo && (
          <Breadcrumb
            filename={ds.filename}
            activeSheet={ds.activeSheet}
            activeSection={activeSection}
            onNavigate={scrollToSection}
          />
        )}

        {/* ── Section navigation strip ───────────── */}
        {ds.hasData && ds.currentSheetInfo && (
          <>
            <div className="flex items-center justify-between gap-2 border-b mb-6 -mx-1 overflow-x-auto">
              <nav aria-label="Section navigation" className="flex items-center gap-1" data-testid="section-nav">
                {SECTION_TABS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => scrollToSection(id)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors btn-press ${
                      activeSection === id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                    data-testid={`section-tab-${id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </nav>
              {ds.sheetNames.length >= 2 && (
                <Button
                  variant={compareOpen ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setCompareOpen((v) => !v)}
                  className="shrink-0 mb-1"
                  data-testid="toggle-compare-btn"
                >
                  <GitCompareArrows className="w-3.5 h-3.5 mr-1.5" />
                  {t('compare.title')}
                </Button>
              )}
            </div>

            {/* ── Sheet comparison (togglable) ───── */}
            {compareOpen && ds.sheetNames.length >= 2 && (
              <div className="mb-6">
                <Suspense fallback={<SectionFallback />}>
                  <SheetComparison
                    sheetNames={ds.sheetNames}
                    sheetsInfo={ds.sheetsInfo}
                    onClose={() => setCompareOpen(false)}
                  />
                </Suspense>
              </div>
            )}

            {/* ── Content + chat sidebar ─────────── */}
            <div className="flex gap-4 md:gap-6">
              {/* Main column */}
              <div className="flex-1 min-w-0 space-y-6 md:space-y-8 pb-10">
                {/* Section 2: Sheets Overview */}
                <div ref={overviewRef}>
                  <Suspense fallback={<SectionFallback />}>
                    <SheetsOverviewSection
                      sheetNames={ds.sheetNames}
                      activeSheet={ds.activeSheet}
                      sheetsInfo={ds.sheetsInfo}
                      onSheetChange={ds.switchSheet}
                      currentSheetInfo={ds.currentSheetInfo}
                      workbookOverview={ds.workbookOverview}
                      filename={ds.filename}
                    />
                  </Suspense>
                </div>

                {/* Section 3: Dashboard */}
                <div ref={dashboardRef}>
                  <Suspense fallback={<SectionFallback />}>
                    <DashboardSection
                      currentSheetInfo={ds.currentSheetInfo}
                      currentSheetAnalysis={ds.currentSheetAnalysis}
                      onAnalyze={ds.analyzeCurrentSheet}
                      analysisLoading={ds.analysisLoading}
                      analysisError={ds.analysisError}
                    />
                  </Suspense>
                </div>

                {/* Section 5: Export */}
                <div ref={exportRef}>
                  <Suspense fallback={<SectionFallback />}>
                    <ExportSection
                      datasetId={ds.datasetId}
                      sheetName={ds.activeSheet}
                      backendUrl={BACKEND_URL}
                      previewData={ds.currentSheetInfo?.preview}
                      columns={ds.currentSheetInfo?.columns}
                    />
                  </Suspense>
                </div>
              </div>

              {/* Section 4: Chat with Data (sidebar) */}
              <Suspense fallback={null}>
                <ChatSection
                  chatHistory={chatHistory}
                  onSendMessage={handleSendMessage}
                  onClose={() => setChatOpen(false)}
                  sheetName={ds.activeSheet}
                  hasApiKey={!!apiKey}
                  onOpenSettings={() => setSettingsOpen(true)}
                  chatOpen={chatOpen}
                />
              </Suspense>
            </div>
          </>
        )}
      </main>

      {/* ── Footer ───────────────────────────────── */}
      <Footer />

      {/* ── Settings dialog ──────────────────────── */}
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        apiKey={apiKey}
        setApiKey={setApiKey}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        backendUrl={BACKEND_URL}
      />

      {/* ── Onboarding tour (first visit) ────────── */}
      <OnboardingTour />

      {/* ── Confirm replace dialog ───────────────── */}
      <ConfirmDialog
        open={!!confirmReplace}
        title={t('confirm.replaceTitle')}
        description={t('confirm.replaceDesc')}
        confirmLabel={t('confirm.replaceBtn')}
        onConfirm={confirmReplaceAction}
        onCancel={() => setConfirmReplace(null)}
        variant="default"
      />

      {/* ── Command palette (Ctrl+K) ─────────────── */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onNavigate={(id) => scrollToSection(id)}
        onChatToggle={() => setChatOpen(!chatOpen)}
        onSettingsOpen={() => setSettingsOpen(true)}
        onThemeToggle={() => {
          const root = document.documentElement;
          const isDark = root.classList.contains('dark');
          if (isDark) { root.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
          else { root.classList.add('dark'); localStorage.setItem('theme', 'dark'); }
        }}
        sheetNames={ds.sheetNames}
        onSheetChange={ds.switchSheet}
        hasData={ds.hasData}
        isDark={typeof document !== 'undefined' && document.documentElement.classList.contains('dark')}
      />
    </div>
  );
}

export default App;
